import { useState, useEffect } from 'react';

import { Box, Stack, Button, Divider, Collapse, Pagination, Typography } from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { useAppDispatch } from 'src/store/store';
import { openTaskEditorSidesheet } from 'src/store/slices/app/appSlice';
import { useGetLinkedTasksQuery } from 'src/services/tasks/tasks.service';
import { TaskStatus, TaskLinkedEntityType } from 'src/services/tasks/tasks.types';

import Iconify from 'src/components/iconify';

import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';
import { useLinkedCalls, LinkedCallItem } from 'src/sections/call-lists/components/linked-calls';

import { TenantType } from 'src/types/enums';
import { FeatureType } from 'src/types/subscription.types';

import LinkedTaskItem from './linked-task-item';

// ----------------------------------------------------------------------

const PER_PAGE = 5;

// Each record type pre-fills, and locks, its own field in the task drawer.
const ADD_TASK_PAYLOAD: Record<TaskLinkedEntityType, (id: string) => Parameters<typeof openTaskEditorSidesheet>[0]> = {
  [TaskLinkedEntityType.CONTACT]: (id) => ({ contactId: id }),
  [TaskLinkedEntityType.COMPANY_PROFILE]: (id) => ({ companyId: id }),
  [TaskLinkedEntityType.CANDIDATE]: (id) => ({ candidateId: id }),
  [TaskLinkedEntityType.JOB]: (id) => ({ jobId: id }),
  [TaskLinkedEntityType.JOB_APPLICATION]: (id) => ({ applicationId: id }),
};

type Props = {
  entityType: TaskLinkedEntityType;
  entityId: string;
};

/**
 * The Tasks block at the top of a profile's Notes card (JT1-1796). Open tasks come first, soonest
 * due first; completed ones sit behind a toggle. Every member of the organization sees every task
 * linked to the record, whoever it is assigned to. On contacts and candidates the next call on each
 * call list comes first (JT1-1827): calls live on the call list, tasks are the actions.
 */
export default function LinkedTasks({ entityType, entityId }: Props) {
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const tenantType = useOrgTenant();
  const { canAccess } = useFeatures();

  const showCompleted = useBoolean();
  const [openPage, setOpenPage] = useState(1);
  const [completedPage, setCompletedPage] = useState(1);

  const enabled = !!entityId
    && tenantType !== TenantType.Candidate
    && canAccess(FeatureType.TASK_MANAGEMENT_ENABLED);

  const { currentData: openTasks } = useGetLinkedTasksQuery({
    entityType,
    entityId,
    status: TaskStatus.PENDING,
    page: openPage,
    perPage: PER_PAGE,
  }, { skip: !enabled });

  // Always fetched, so the toggle can show how many there are.
  const { currentData: completedTasks } = useGetLinkedTasksQuery({
    entityType,
    entityId,
    status: TaskStatus.COMPLETED,
    page: completedPage,
    perPage: PER_PAGE,
  }, { skip: !enabled });

  const calls = useLinkedCalls(entityType, entityId);

  // Completing or removing the last task on a page leaves it empty; step back a page.
  useEffect(() => {
    if (openTasks && openTasks.results.length === 0 && openPage > 1) {
      setOpenPage((page) => page - 1);
    }
  }, [openTasks, openPage]);

  useEffect(() => {
    if (completedTasks && completedTasks.results.length === 0 && completedPage > 1) {
      setCompletedPage((page) => page - 1);
    }
  }, [completedTasks, completedPage]);

  if (!enabled) return null;

  const openCount = openTasks?.count ?? 0;
  const completedCount = completedTasks?.count ?? 0;

  return (
    <Box sx={{ my: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Iconify icon="mdi:clipboard-check-outline" width={18} />
          <Typography variant="subtitle2">{t('tasks.linked.title')}</Typography>
          {openTasks && (
            <Typography variant="caption" color="text.secondary">
              {t('tasks.linked.open_count', { count: openCount })}
            </Typography>
          )}
        </Stack>

        <Button
          size="small"
          startIcon={<Iconify icon="eva:plus-fill" />}
          onClick={() => dispatch(openTaskEditorSidesheet(ADD_TASK_PAYLOAD[entityType](entityId)))}
        >
          {t('tasks.actions.add')}
        </Button>
      </Stack>

      {calls.length > 0 && (
        <Stack spacing={1} sx={{ mb: openCount > 0 ? 1 : 0 }}>
          {calls.map((call) => <LinkedCallItem key={call.entryId} call={call} />)}
        </Stack>
      )}

      {openTasks && openCount === 0 && !calls.length && (
        <Typography variant="body2" color="text.secondary">
          {t('tasks.linked.empty')}
        </Typography>
      )}

      {openCount > 0 && (
        <Stack spacing={1}>
          {openTasks?.results.map((task) => <LinkedTaskItem key={task.id} task={task} />)}
        </Stack>
      )}

      {openCount > PER_PAGE && (
        <Stack direction="row" justifyContent="center" sx={{ mt: 1 }}>
          <Pagination
            size="small"
            count={Math.ceil(openCount / PER_PAGE)}
            page={openPage}
            onChange={(_event, page) => setOpenPage(page)}
          />
        </Stack>
      )}

      {completedCount > 0 && (
        <>
          <Button
            size="small"
            color="inherit"
            onClick={showCompleted.onToggle}
            endIcon={<Iconify icon={showCompleted.value ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'} />}
            sx={{ mt: 1, color: 'text.secondary' }}
          >
            {showCompleted.value
              ? t('tasks.linked.hide_completed')
              : t('tasks.linked.show_completed', { count: completedCount })}
          </Button>

          <Collapse in={showCompleted.value} unmountOnExit>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {completedTasks?.results.map((task) => <LinkedTaskItem key={task.id} task={task} />)}
            </Stack>

            {completedCount > PER_PAGE && (
              <Stack direction="row" justifyContent="center" sx={{ mt: 1 }}>
                <Pagination
                  size="small"
                  count={Math.ceil(completedCount / PER_PAGE)}
                  page={completedPage}
                  onChange={(_event, page) => setCompletedPage(page)}
                />
              </Stack>
            )}
          </Collapse>
        </>
      )}

      <Divider sx={{ mt: 2 }} />
    </Box>
  );
}
